import React, { useState, useEffect, useCallback, useRef } from 'react';
import './index.css';
import BuilderApp from './BuilderApp';
import AquaApp from './AquaApp';
import HotelApp from './HotelApp';
import HospitalApp from './HospitalApp';
import HospitalPatientPortalApp from './HospitalPatientPortalApp';
import HotelOnlineMenu from './pages/hotel/HotelOnlineMenu';
import HospitalPublicPage from './HospitalPublicPage';
import MarketApp from './market/MarketApp';
import MatrimonyApp from './matrimony/MatrimonyApp';
import Dashboard from './pages/Dashboard';
import Students from './pages/Students';
import Teachers from './pages/Teachers';
import Attendance from './pages/Attendance';
import Marks from './pages/Marks';
import Fees from './pages/Fees';
import Salaries from './pages/Salaries';
import Holidays from './pages/Holidays';
import ClassAssignment from './pages/ClassAssignment';
import OnlineTests from './pages/OnlineTests';
import StudentPortal from './pages/StudentPortal';
import UserRegistration from './pages/UserRegistration';
import ViewEmployees from './pages/ViewEmployees';
import ChangePassword from './pages/ChangePassword';
import DocManager from './pages/DocManager';

// Clears the server session and redirects to login
function forceLogout(reason) {
  fetch('/spring/aqua/api/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
  fetch('/spring/logout', { credentials: 'include' }).catch(() => {});
  const url = '/spring/login' + (reason ? '?info=' + encodeURIComponent(reason) : '');
  window.location.replace(url);
}

// Catches runtime crashes inside any business-specific app and logs the user out
class BizErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch(error) {
    console.error('[BizErrorBoundary] Module crashed:', error);
    forceLogout('module_error');
  }
  render() {
    if (this.state.crashed) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: '100vh', color: '#5f6368', fontSize: 15 }}>
          <div style={{ marginBottom: 8 }}>⚠️ Module unavailable. Redirecting to login…</div>
        </div>
      );
    }
    return this.props.children;
  }
}

const NAV = [
  { section: '🏠 Main', items: [
    { key: 'dashboard',   label: '🏠 Dashboard',       icon: 'fas fa-th-large',           badge: null },
  ]},
  { section: '⚙️ Administration', items: [
    { key: 'holidays',        label: '🗓️ Holiday Calendar',   icon: 'fas fa-calendar-alt',  badge: null },
    { key: 'registration',   label: '👤 User Registration',  icon: 'fas fa-user-plus',     badge: null },
    { key: 'viewemployees',  label: '👥 View Users',           icon: 'fas fa-users',         badge: null },
    { key: 'changepassword', label: '🔑 Change Password',     icon: 'fas fa-key',           badge: null },
  ]},
  { section: '📝 Online Tests', items: [
    { key: 'onlinetests',    label: '📝 Test Management',  icon: 'fas fa-tasks',         badge: null },
    { key: 'studentportal',  label: '🖥️ Student Portal',   icon: 'fas fa-user-check',    badge: null },
  ]},
  { section: '📚 Academics', items: [
    { key: 'students',    label: '🎓 Students',         icon: 'fas fa-user-graduate',      badge: '420' },
    { key: 'teachers',    label: '👩‍🏫 Teachers',         icon: 'fas fa-chalkboard-teacher', badge: null },
    { key: 'assignments', label: '🏫 Class Assignment', icon: 'fas fa-school',             badge: null },
    { key: 'attendance',  label: '📅 Attendance',       icon: 'fas fa-calendar-check',     badge: null },
    { key: 'marks',       label: '📊 Marks & Results',  icon: 'fas fa-chart-bar',          badge: null },
  ]},
  { section: '💰 Finance', items: [
    { key: 'fees',        label: '💰 Fees',             icon: 'fas fa-rupee-sign',         badge: '12' },
    { key: 'salaries',    label: '💼 Salaries',         icon: 'fas fa-wallet',             badge: null },
  ]},
  { section: '📁 Documents', items: [
    { key: 'docmanager',  label: '📁 Document Manager', icon: 'fas fa-folder-open',        badge: null },
  ]},
];

const PAGES = {
  dashboard:   <Dashboard />,
  students:    <Students />,
  teachers:    <Teachers />,
  assignments: <ClassAssignment />,
  attendance:  <Attendance />,
  marks:       <Marks />,
  fees:        <Fees />,
  salaries:    <Salaries />,
  holidays:    <Holidays />,
  onlinetests:    <OnlineTests />,
  studentportal:  <StudentPortal />,
  registration:   <UserRegistration />,
  viewemployees:  <ViewEmployees />,
  changepassword: <ChangePassword />,
  docmanager:     <DocManager />,
};

const TITLES = {
  dashboard:   '🏠 Dashboard',
  students:    '🎓 Student Management',
  teachers:    '👩‍🏫 Teacher Management',
  assignments: '🏫 Class-Teacher Assignment',
  attendance:  '📅 Attendance Management',
  marks:       '📊 Marks & Results',
  fees:        '💰 Fee Management',
  salaries:    '💼 Salary Management',
  holidays:    '🗓️ Holiday Calendar',
  onlinetests:    '📝 Online Test Management',
  studentportal:  '🖥️ Student Test Portal',
  registration:   '👤 User Registration',
  viewemployees:  '👥 View Users',
  changepassword: '🔑 Change Password',
  docmanager:     '📁 Document Manager',
};

const STUDENT_NAV = [
  { key: 'studentportal',  icon: '🖥️', label: 'My Tests' },
  { key: 'changepassword', icon: '🔑', label: 'Change Password' },
];

// Breakpoint: below this width the sidebar behaves as a drawer overlay
const DESKTOP_BP = 1100;

// Idle session timeout: 30 min total, warn when 2 min remain
const IDLE_LIMIT_MS  = 30 * 60 * 1000;
const IDLE_WARN_SECS = 120;

function SmabizzFooter() {
  return (
    <footer style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 900,
      background: '#002366', borderTop: '2px solid rgba(255,255,255,0.15)',
      textAlign: 'center', padding: '8px 16px',
    }}>
      <p style={{ color: '#9ea7b4', fontFamily: "'Roboto', sans-serif", fontSize: 12, fontWeight: 400, letterSpacing: '.3px', margin: 0 }}>
        &copy; {new Date().getFullYear()} All rights reserved &mdash; SmaBizZ Cloud Products &nbsp;|&nbsp;
        <a href="https://smabizz.com" style={{ color: '#4CCEE8', textDecoration: 'none', fontWeight: 500 }}>smabizz.com</a>
      </p>
    </footer>
  );
}

function initials(name) {
  if (!name) return 'AD';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

const BACKEND_BASE = window.location.port === '3000' ? 'http://localhost:8080' : '';
const RETURN_URL   = window.location.origin;

function redirectToLogin() {
  window.location.href = `${BACKEND_BASE}/spring/login?returnTo=${encodeURIComponent(RETURN_URL)}`;
}

function getInitialPage() {
  try {
    const p = new URLSearchParams(window.location.search).get('page');
    if (p && PAGES[p]) return p;
  } catch (_) {}
  return 'dashboard';
}

// Public hotel menu detection — no auth required
// Supports path  /public/{ownersid}/menu  or query ?hotelMenu={ownersid}
function getHotelMenuParam() {
  try {
    const qp = new URLSearchParams(window.location.search).get('hotelMenu');
    if (qp) return qp;
    const m = window.location.pathname.match(/\/public\/([^/]+)\/menu/);
    if (m) return m[1];
  } catch (_) {}
  return null;
}

// Public hospital booking page — no auth required
// Query: ?hospitalPublic={ownersid}
function getHospitalPublicParam() {
  try {
    const qp = new URLSearchParams(window.location.search).get('hospitalPublic');
    if (qp) return qp;
  } catch (_) {}
  return null;
}

export default function App() {
  const path = window.location.pathname;

  // Matrimony app — served at /app/matrimony/*
  if (path === '/app/matrimony' || path.startsWith('/app/matrimony/')) {
    return <MatrimonyApp />;
  }

  // Market app — served at /app/market/*
  if (path === '/app/market' || path.startsWith('/app/market/')) {
    return <MarketApp />;
  }

  // Public hotel online menu — no auth needed
  const hotelMenuOwnersid = getHotelMenuParam();
  if (hotelMenuOwnersid) {
    return <HotelOnlineMenu ownersid={hotelMenuOwnersid} />;
  }

  // Public hospital booking + registration — no auth needed
  const hospitalPublicOwnersid = getHospitalPublicParam();
  if (hospitalPublicOwnersid) {
    return <HospitalPublicPage ownersid={hospitalPublicOwnersid} />;
  }

  return <AppMain />;
}

function AppMain() {
  const [active, setActive]       = useState(getInitialPage);
  const [studentActive, setStudentActive] = useState('studentportal');
  const [studentNavOpen, setStudentNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(true);    // desktop: icon-only vs full — starts closed
  const [drawerOpen, setDrawerOpen] = useState(false); // tablet/mobile: overlay drawer
  const [hovered, setHovered]     = useState(false);   // hover-to-expand on collapsed desktop
  const [user, setUser]           = useState(null);
  const [authErr, setAuthErr]     = useState('');
  const [clock, setClock]         = useState('');
  const [theme, setTheme]         = useState(() => localStorage.getItem('smabizz-theme') || 'light');
  const [idleWarning, setIdleWarning] = useState(null); // null = hidden; number = seconds left
  const [filesImpl, setFilesImpl] = useState('JSP');   // JSP or REACTJS — from server config
  const lastActivityRef = useRef(Date.now());

  const toggleTheme = () => setTheme(t => {
    const next = t === 'light' ? 'dark' : 'light';
    localStorage.setItem('smabizz-theme', next);
    return next;
  });

  useEffect(() => {
    const tick = () => setClock(
      new Date().toLocaleTimeString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: true,
      })
    );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // On tablet/mobile, sidebar is always in icon-only (collapsed) mode;
  // the drawerOpen overlay shows the full 240px panel.
  useEffect(() => {
    const onResize = () => {
      const w = window.innerWidth;
      if (w < DESKTOP_BP) {
        setCollapsed(true);      // force icon-only on narrow screens
        if (w >= DESKTOP_BP) setDrawerOpen(false);
      }
      if (w >= DESKTOP_BP) {
        setDrawerOpen(false);    // close any open overlay when going back to desktop
      }
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close drawer when pressing Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Session management
  const checkSession = useCallback(() =>
    fetch('/spring/school/auth/me', { credentials: 'include' })
      .then(res => {
        if (res.status === 401) { redirectToLogin(); return null; }
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then(data => { if (data) setUser(data); })
      .catch(err => setAuthErr(err.message)),
  []);

  useEffect(() => { checkSession(); }, [checkSession]);

  // Load server-side feature flags once on mount
  useEffect(() => {
    fetch('/spring/app/config', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(cfg => { if (cfg?.filesImpl) setFilesImpl(cfg.filesImpl); })
      .catch(() => {});
  }, []);

  // Periodic re-validation every 5 minutes
  useEffect(() => {
    const id = setInterval(() => {
      fetch('/spring/school/auth/me', { credentials: 'include' })
        .then(res => { if (res.status === 401) redirectToLogin(); });
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Re-validate on each page switch
  useEffect(() => {
    if (!user) return;
    fetch('/spring/school/auth/me', { credentials: 'include' })
      .then(res => { if (res.status === 401) redirectToLogin(); });
  }, [active, user]);

  // Track any user activity and reset idle clock
  useEffect(() => {
    const resetIdle = () => {
      lastActivityRef.current = Date.now();
      setIdleWarning(null);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(ev => document.addEventListener(ev, resetIdle, { passive: true }));
    return () => events.forEach(ev => document.removeEventListener(ev, resetIdle));
  }, []);

  // Idle timeout checker — runs every second once user is authenticated
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      const idleSecs = Math.floor((Date.now() - lastActivityRef.current) / 1000);
      const remaining = Math.ceil((IDLE_LIMIT_MS / 1000) - idleSecs);
      if (remaining <= 0) {
        redirectToLogin();
      } else if (remaining <= IDLE_WARN_SECS) {
        setIdleWarning(remaining);
      }
    }, 1000);
    return () => clearInterval(id);
  }, [user]);

  const handleNavClick = (key) => {
    if (key === 'docmanager') {
      if (filesImpl === 'REACTJS') {
        setActive('docmanager');
        setDrawerOpen(false);
        if (window.innerWidth >= DESKTOP_BP) setCollapsed(true);
      } else {
        window.location.href = `${BACKEND_BASE}/spring/s3/gdocview`;
      }
      return;
    }
    setActive(key);
    setDrawerOpen(false);               // close drawer on tablet/mobile
    if (window.innerWidth >= DESKTOP_BP) setCollapsed(true); // collapse to icon-only on desktop
  };

  const handleLogout = () => {
    window.location.href = `${BACKEND_BASE}/spring/logout`;
  };

  const stayLoggedIn = () => {
    lastActivityRef.current = Date.now();
    setIdleWarning(null);
  };

  const toggleDesktop = () => setCollapsed(c => !c);
  const toggleDrawer  = () => setDrawerOpen(d => !d);

  // Show labels when sidebar is expanded, hovered while collapsed, or drawer is open
  const showLabels = !collapsed || drawerOpen || (collapsed && hovered);

  // Idle warning modal — rendered into whichever layout is active
  const idleModal = idleWarning !== null ? (
    <div className="idle-overlay" role="dialog" aria-modal="true" aria-label="Session expiring">
      <div className="idle-modal">
        <div className="idle-countdown" style={{
          background: idleWarning <= 30 ? 'linear-gradient(135deg,#c53030,#9c2626)' : 'linear-gradient(135deg,#c05621,#c53030)'
        }}>
          {idleWarning}s
        </div>
        <div className="idle-title">Session Expiring</div>
        <div className="idle-msg">
          You've been inactive. You'll be automatically signed out in <strong>{idleWarning} second{idleWarning !== 1 ? 's' : ''}</strong>.
        </div>
        <div className="idle-actions">
          <button className="btn btn-primary" onClick={stayLoggedIn}>
            <i className="fas fa-check" style={{ marginRight: 6 }}></i>Stay Signed In
          </button>
          <button className="btn btn-outline" onClick={handleLogout}>
            Sign Out Now
          </button>
        </div>
      </div>
    </div>
  ) : null;

  if (user === null && !authErr) {
    return (
      <div className="loading-screen">
        <i className="fas fa-graduation-cap" style={{ fontSize: 48, color: '#0066CC' }}></i>
        <div style={{ fontSize: 14, color: '#718096', marginTop: 12 }}>Verifying session…</div>
      </div>
    );
  }

  if (authErr) {
    return (
      <div className="loading-screen">
        <div style={{ color: '#c53030', fontSize: 14 }}>Could not connect to server: {authErr}</div>
        <button className="btn btn-primary" style={{ marginTop: 12 }} onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  const displayName = user.username || 'Student';
  const isDesktop   = window.innerWidth >= DESKTOP_BP;
  const isStudent   = user.role?.includes('role_student');
  const isBuilder   = user.business === 'builder' || user.role?.includes('role_builder');
  const isAqua      = user.business === 'aqua'    || user.role?.includes('role_aqua');
  const isHotel     = user.business === 'hotel'   || user.role?.includes('role_hotel');
  const isHospital        = user.business === 'hospital' || user.role?.includes('role_hospital');
  const isHospitalPatient = user.business === 'hospital_patient';

  /* ── Builder layout ─────────────────────────────────────── */
  if (isBuilder) {
    return <BizErrorBoundary><BuilderApp user={user} handleLogout={handleLogout} /></BizErrorBoundary>;
  }

  /* ── Aquaculture (CHMR) layout ───────────────────────────── */
  if (isAqua) {
    return <BizErrorBoundary><AquaApp user={user} /></BizErrorBoundary>;
  }

  /* ── Hotel Management layout ─────────────────────────────── */
  if (isHotel) {
    return <BizErrorBoundary><HotelApp user={user} /></BizErrorBoundary>;
  }

  /* ── Hospital Patient Portal ────────────────────────────── */
  if (isHospitalPatient) {
    return <BizErrorBoundary><HospitalPatientPortalApp user={user} /></BizErrorBoundary>;
  }

  /* ── Hospital Management layout ─────────────────────────── */
  if (isHospital) {
    return <BizErrorBoundary><HospitalApp user={user} /></BizErrorBoundary>;
  }

  /* ── Document Management (files) layout ─────────────────── */
  if (user.business === 'files') {
    return <BizErrorBoundary><DocManager user={user} /></BizErrorBoundary>;
  }

  /* ── Unknown / mismatched business type ─────────────────── */
  // If business is set to something non-school (e.g. session from a different module
  // whose package isn't deployed), logout rather than silently loading school data.
  const SCHOOL_BUSINESS_TYPES = ['', 'school'];
  if (!SCHOOL_BUSINESS_TYPES.includes(user.business || '')) {
    forceLogout('wrong_module');
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', minHeight: '100vh', color: '#5f6368', fontSize: 15 }}>
        <div>⚠️ Module not available for this account. Redirecting to login…</div>
      </div>
    );
  }

  /* ── Student layout (with left nav sidebar) ──────────────── */
  if (isStudent) {
    return (
      <div data-theme={theme} className="student-wrap">

        {idleModal}

        {/* Backdrop — touch outside to close on mobile */}
        {studentNavOpen && (
          <div className="sidebar-backdrop student-nav-backdrop" onClick={() => setStudentNavOpen(false)} />
        )}

        {/* Left sidebar — collapsed by default, expands on hover / tap */}
        <aside
          className={`student-sidenav${studentNavOpen ? ' expanded' : ''}`}
          onMouseEnter={() => setStudentNavOpen(true)}
          onMouseLeave={() => setStudentNavOpen(false)}
          onClick={() => { if (!studentNavOpen) setStudentNavOpen(true); }}
        >
          <div className="student-sidenav-brand">
            <i className="fas fa-graduation-cap" style={{ fontSize: 18, color: '#63b3ed', flexShrink: 0 }}></i>
            <span className="student-sidenav-brand-text">SmaBizZ</span>
          </div>
          <nav className="student-sidenav-nav">
            {STUDENT_NAV.map(item => (
              <div
                key={item.key}
                className={`student-sidenav-item${studentActive === item.key ? ' active' : ''}`}
                onClick={e => { e.stopPropagation(); setStudentActive(item.key); setStudentNavOpen(false); }}
                title={item.label}
              >
                <span className="student-sidenav-icon">{item.icon}</span>
                <span className="student-sidenav-label">{item.label}</span>
              </div>
            ))}
            <div
              className="student-sidenav-item student-sidenav-signout"
              onClick={e => { e.stopPropagation(); handleLogout(); }}
              title="Sign Out"
            >
              <span className="student-sidenav-icon">🚪</span>
              <span className="student-sidenav-label">Sign Out</span>
            </div>
          </nav>
          <div className="student-sidenav-footer">
            <div className="chip-avatar" style={{ width: 30, height: 30, fontSize: 11, flexShrink: 0 }}>
              {initials(displayName)}
            </div>
            <span className="student-sidenav-username">{displayName}</span>
          </div>
        </aside>

        {/* Right: topbar + content */}
        <div className="student-area">
          <div className="student-topbar">
            {user.businesstitle
              ? <span className="student-topbar-title">{user.businesstitle}</span>
              : <span className="student-topbar-title">{
                  studentActive === 'changepassword' ? '🔑 Change Password' : '🖥️ My Tests'
                }</span>
            }
            <div className="student-topbar-actions">
              <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme">
                {theme === 'dark' ? '☀️' : '🌙'}
              </button>
              <button className="topbar-btn" onClick={handleLogout} title="Sign Out">
                <i className="fas fa-sign-out-alt"></i>
                <span className="student-topbar-signout-label"> Sign Out</span>
              </button>
            </div>
          </div>
          <div className="student-content">
            {PAGES[studentActive]}
          </div>
        </div>

        <SmabizzFooter />
      </div>
    );
  }

  /* ── Admin / Teacher layout (with sidebar) ────────────────── */
  return (
    <div className="app" data-theme={theme}>

      {idleModal}

      {/* ── Backdrop (mobile / tablet overlay) ─────────────────── */}
      {drawerOpen && (
        <div className="sidebar-backdrop" onClick={() => setDrawerOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────── */}
      <aside
        className={[
          'sidebar',
          collapsed && !drawerOpen && !hovered ? 'collapsed' : '',
          drawerOpen ? 'drawer-open' : '',
          collapsed && hovered ? 'hover-expanded' : '',
        ].filter(Boolean).join(' ')}
        onMouseEnter={() => { if (window.innerWidth >= DESKTOP_BP) setHovered(true); }}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Brand header */}
        <div className="sidebar-brand">
          <div className="brand-icon"><i className="fas fa-graduation-cap"></i></div>
          {showLabels && (
            <div className="brand-text">
              SmaBizZ<span>School Management</span>
            </div>
          )}
          {/* Desktop toggle — hidden on tablet/mobile via CSS */}
          <button className="sidebar-toggle desktop-only" onClick={toggleDesktop} title={collapsed ? 'Expand' : 'Collapse'}>
            <i className="fas fa-bars"></i>
          </button>
          {/* Drawer close button — visible when drawer is open on tablet/mobile */}
          {drawerOpen && (
            <button className="sidebar-toggle" onClick={() => setDrawerOpen(false)} title="Close menu">
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {NAV.map(group => (
            <div key={group.section}>
              {showLabels && <div className="nav-section">{group.section}</div>}
              {group.items.map(item => {
                const sp = item.label.indexOf(' ');
                const emoji = sp > 0 ? item.label.slice(0, sp) : item.label;
                const text  = sp > 0 ? item.label.slice(sp + 1) : '';
                return (
                  <div
                    key={item.key}
                    className={`nav-item${active === item.key ? ' active' : ''}`}
                    onClick={() => handleNavClick(item.key)}
                    title={!showLabels ? item.label : undefined}
                  >
                    <span className="nav-icon" style={{ fontSize: 16 }}>{emoji}</span>
                    {showLabels && <span className="nav-label">{text}</span>}
                    {showLabels && item.badge && (
                      <span className="nav-badge">{item.badge}</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {/* Signout */}
          <div style={{ marginTop: 'auto', paddingTop: 8 }}>
            {showLabels && <div className="nav-section">⚙️ Account</div>}
            <div
              className="nav-item"
              onClick={handleLogout}
              title={!showLabels ? '🚪 Sign Out' : undefined}
              style={{ color: '#fc8181' }}
            >
              <span className="nav-icon" style={{ fontSize: 16 }}>🚪</span>
              {showLabels && <span className="nav-label" style={{ color: '#fc8181' }}>Sign Out</span>}
            </div>
          </div>
        </nav>

        {/* Footer user info */}
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="chip-avatar" style={{ width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>
              {initials(displayName)}
            </div>
            {showLabels && (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayName}
                  </div>
                  <div style={{ fontSize: 11, color: '#718096', textTransform: 'capitalize' }}>
                    {user.role?.replace('role_', '') || 'User'}
                  </div>
                </div>
                <button className="sidebar-logout" onClick={handleLogout} title="Logout">
                  <i className="fas fa-sign-out-alt"></i>
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* ── Main content ─────────────────────────────────────────── */}
      <div
        className={`main${isDesktop && collapsed ? ' expanded' : ''}`}
        onClick={() => {
          if (drawerOpen) setDrawerOpen(false);
          else if (!collapsed) setCollapsed(true);
        }}
      >

        {/* Business title banner */}
        {user.businesstitle && (
          <div className="business-title-banner">
            <span>{user.businesstitle}</span>
          </div>
        )}

        {/* Topbar */}
        <div className="topbar">
          {/* Hamburger — shown on tablet/mobile only (CSS hides on desktop) */}
          <button className="topbar-hamburger" onClick={toggleDrawer} title="Menu">
            <i className={`fas ${drawerOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </button>

          <div className="topbar-title">
            {TITLES[active]}
            <span className="topbar-subtitle" style={{ fontVariantNumeric: 'tabular-nums' }}>🕐 {clock} IST</span>
          </div>

          <div className="topbar-right">
            <div className="search-bar topbar-search">
              <span style={{ fontSize: 15 }}>🔍</span>
              <input type="text" placeholder="Search students, teachers…" />
            </div>
            <button className="topbar-btn" title={theme === 'dark' ? 'Light mode' : 'Dark mode'} onClick={toggleTheme}>
              {theme === 'dark' ? '☀️' : '🌙'}
            </button>
            <button className="topbar-btn" title="Notifications">
              🔔
            </button>
            <button className="topbar-btn" title="Logout" onClick={handleLogout}>
              🚪
            </button>
            <div className="avatar" title={displayName}>{initials(displayName)}</div>
          </div>
        </div>

        {/* Page content */}
        <div className="content" style={{ paddingBottom: '52px' }}>
          {PAGES[active]}
        </div>
      </div>
      <SmabizzFooter />
    </div>
  );
}
